import { createContext, useContext, useMemo, ReactNode } from "react";
import { useLocation } from "react-router-dom";
import { useLop } from "../hooks/useLop";
import { useHe } from "../hooks/useHe";
import { useAuth } from "../hooks/useAuth";
import { getBasePathSegment, getLocationBaseSegment } from "../utils/routePath";
import { IDmHe } from "../model/respone/category/IDmHe";
import { IStuLopViewModel } from "../model/respone/category/IStuLopViewModel";
import { IMenuViewModel } from "../model/respone/auth/IMenuViewModel";

type AuthDataContextType = {
    children: ReactNode;
};

/** Store Lop - có thể tách file riêng sau */
export type AuthDataLopStoreType = {
    lops: IStuLopViewModel[];
    dataHe: IDmHe[];
    listIdHe: number[];
    isLoading: boolean;
};

/** Dữ liệu nội bộ để tính phạm vi theo route (không expose ra ngoài, giữ API cũ của useLopContext) */
type AuthDataLopInternalType = {
    globalLops: IStuLopViewModel[];
    heMap: Map<number, IDmHe>;
    /** base-segment route -> tập id_lop được phân quyền cho menu đó */
    scopeMap: Record<string, Set<number>>;
    /** Phạm vi mặc định (toàn ứng dụng) cho trang không gán riêng. null = không giới hạn (dùng global) */
    defaultScope: Set<number> | null;
    isLoading: boolean;
};

/** Store tổng - mở rộng thêm he, chuyenNganh... khi cần */
type AuthDataStoreType = {
    lop: AuthDataLopInternalType;
};

const AuthDataContext = createContext<AuthDataStoreType | null>(null);
AuthDataContext.displayName = "AuthDataContext";

/** Duyệt cây menu, gom id_lops của menu có gán phạm vi theo base-segment route */
const buildScopeMap = (menus: IMenuViewModel[]): Record<string, Set<number>> => {
    const map: Record<string, Set<number>> = {};
    const walk = (menu: IMenuViewModel) => {
        if (menu.id_lops && menu.id_lops.length > 0) {
            const key = getBasePathSegment(menu.path);
            if (key) {
                if (!map[key]) map[key] = new Set<number>();
                menu.id_lops.forEach((id) => map[key].add(id));
            }
        }
        menu.items?.forEach(walk);
    };
    menus?.forEach(walk);
    return map;
};

const useAuthDataContext = () => {
    const context = useContext(AuthDataContext);
    if (!context) {
        throw new Error("useAuthDataContext must be used within AuthDataProvider");
    }
    return context;
};

/**
 * Hook lấy data Lop - route-aware. API giữ nguyên { lops, dataHe, listIdHe, isLoading } cho mọi component hiện tại.
 * Nếu route hiện tại thuộc menu có gán phạm vi -> lọc theo phạm vi đó; ngược lại dùng phạm vi mặc định.
 */
const useLopContext = (): AuthDataLopStoreType => {
    const store = useAuthDataContext().lop;
    const location = useLocation();
    const base = getLocationBaseSegment(location.pathname);

    return useMemo(() => {
        const scopeSet = store.scopeMap[base] ?? store.defaultScope;
        const lops = scopeSet
            ? store.globalLops.filter((l) => scopeSet.has(l.id_lop))
            : store.globalLops;

        // id_he được phân quyền theo phạm vi lớp hiện tại
        const listIdHe: number[] = Array.from(new Set(lops.map((x) => x.id_he)));
        const dataHe: IDmHe[] = listIdHe
            .map((x) => store.heMap.get(x))
            .filter((x): x is IDmHe => x !== undefined)
            .sort((a, b) => a.ten_he.localeCompare(b.ten_he));

        return { lops, dataHe, listIdHe, isLoading: store.isLoading };
    }, [store, base]);
};

const AuthDataProvider = ({ children }: AuthDataContextType) => {
    const lopData = useLop();
    const { heMap } = useHe();
    const { user } = useAuth();

    const scopeMap = useMemo(() => buildScopeMap(user?.menus ?? []), [user?.menus]);
    const defaultScope = useMemo(() => {
        const d = user?.id_lops;
        return d && d.length > 0 ? new Set<number>(d) : null;
    }, [user?.id_lops]);

    const store: AuthDataStoreType = useMemo(
        () => ({
            lop: {
                globalLops: lopData.lops,
                heMap,
                scopeMap,
                defaultScope,
                isLoading: lopData.isLoading,
            },
        }),
        [lopData.lops, lopData.isLoading, heMap, scopeMap, defaultScope]
    );

    return (
        <AuthDataContext.Provider value={store}>
            {children}
        </AuthDataContext.Provider>
    );
};

export {
    AuthDataProvider,
    useLopContext,
    useAuthDataContext,
};
